import React from 'react';

import ReactECharts from 'echarts-for-react';

import { SEMANTIC_COLOR } from '@/shared/design-tokens/design-tokens';
import { escapeHtml } from '@/shared/lib/utils';
import type { ChangeRateType } from '@/shared/ui/ChangeRateIndicator';

import styles from './AssetTypeTrendChart.module.css';

export interface AssetTypeTrendChartProps {
  data: number[];
  changeType: ChangeRateType;
  className?: string;
  dates?: string[];
}

const CHART_COLORS: Record<ChangeRateType, string> = {
  positive: SEMANTIC_COLOR.CONTAINER.COLORFUL.EMERALD.LEVEL4,
  negative: SEMANTIC_COLOR.CONTAINER.COLORFUL.RED.LEVEL4,
  neutral: SEMANTIC_COLOR.CONTAINER.COLORFUL.GRAY.LEVEL4,
} as const;

const getChartColor = (changeType: ChangeRateType): string => {
  return CHART_COLORS[changeType];
};

export const AssetTypeTrendChart: React.FC<AssetTypeTrendChartProps> = ({ data, changeType, className, dates }) => {
  const chartColor = getChartColor(changeType);

  const hasTooltipData = dates && dates.length === data.length;

  const chartOption = {
    grid: {
      left: 0,
      right: 0,
      top: 2,
      bottom: 2,
    },
    tooltip: hasTooltipData
      ? {
          trigger: 'axis',
          backgroundColor: 'var(--opds-sem-color-surface-standard-opacity-level1)',
          borderColor: 'var(--opds-sem-color-surface-standard-level4)',
          borderWidth: 1,
          textStyle: {
            color: 'var(--opds-sem-color-onSurface-standard-level1)',
            fontSize: 12,
          },
          extraCssText: 'backdrop-filter: blur(8px); box-shadow: none;',
          formatter: (params: unknown) => {
            const paramArray = Array.isArray(params) ? params : [params];
            if (paramArray.length === 0 || !dates) return '';

            const param = paramArray[0] as { dataIndex: number; value: number };
            if (param.dataIndex >= dates.length) return '';

            const date = escapeHtml(dates[param.dataIndex]);
            const value = param.value;
            return `${date} - ${value.toLocaleString()}`;
          },
        }
      : undefined,
    xAxis: {
      type: 'category',
      show: false,
      data: data.map((_, index) => index),
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    series: [
      {
        data: data,
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          color: chartColor,
          width: 1.5,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: chartColor + '30',
              },
              {
                offset: 1,
                color: chartColor + '05',
              },
            ],
          },
        },
      },
    ],
    animation: false,
  };

  return (
    <div className={`${styles.assetTypeTrendChart} ${className || ''}`}>
      <ReactECharts option={chartOption} style={{ width: '100%', height: '100%' }} opts={{ renderer: 'svg' }} />
    </div>
  );
};
